import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import MainTemplate from '../MainTemplate_Admin/MainTemplate_Admin';
import { CONTENT_WIDTH, cardSx, colour } from './tokens';

/**
 * The shell every staff screen sits in.
 *
 * The parent screens put their content on a white card over a tinted stage, with the title
 * carried by the page rather than by a centred grey heading. The staff screens had no card at
 * all — content sat straight on the grey body — which is most of why they looked unfinished
 * beside the screens a parent uses.
 *
 *   <AdminPage title="…" lede="…" actions={<Button/>}>…</AdminPage>
 *
 * `wide` lets a table screen use the whole window. The accounts table's compact columns come to
 * well over 2,000px, so any fixed cap made it scroll sideways even on a 1920 screen; prose screens
 * keep the cap, because a line of text across a whole monitor is hard to read (the lede keeps its
 * own 720px measure either way).
 */
const AdminPage = ({ title, lede, actions, wide = false, children }) => {
    const content = (
        <Box sx={{
            bgcolor: colour.stage,
            px: { xs: 0, sm: 2, md: 3 },
            py: { xs: 1, sm: 3 },
            display: 'flex',
            justifyContent: 'center',
        }}>
            {/* minWidth 0: a flex item will not shrink below its content by default, so a table
                wider than the window stretched the whole page — menu and all — sideways instead
                of scrolling inside its own card. With it, the page is the window's width and a
                wide table scrolls within itself. */}
            <Box sx={{ width: '100%', minWidth: 0, maxWidth: wide ? 'none' : CONTENT_WIDTH }}>
                {/* The title and its controls, side by side while they fit.
                    `flexWrap` is what stops the ragged half-wrap: without it the controls stayed on
                    the title's line and wrapped *inside* themselves, so on the reports screen one
                    date field dropped below the other three and the lede broke over two lines
                    around it. Now they either fit beside the title or move, whole, to their own
                    line under it. */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: { xs: 2, sm: 3 },
                    px: { xs: 2, sm: 0 },
                }}>
                    <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                        <Typography variant="h5" sx={{
                            fontWeight: 800,
                            color: colour.ink,
                            fontSize: { xs: '1.35rem', sm: '1.75rem' },
                            letterSpacing: '-0.02em',
                        }}>
                            {title}
                        </Typography>
                        {lede && (
                            <Typography sx={{
                                color: colour.muted,
                                fontSize: { xs: '13px', sm: '14.5px' },
                                mt: 0.5,
                                maxWidth: 720,
                            }}>
                                {lede}
                            </Typography>
                        )}
                    </Box>
                    {actions}
                </Box>

                {children}
            </Box>
        </Box>
    );

    return <MainTemplate content={content} showReEnrolmentGuide={false} />;
};

/** A white card. Sections of a screen are cards, the way they are for a parent. */
export const AdminCard = ({ children, sx = {}, ...rest }) => (
    <Box sx={{ ...cardSx, overflow: 'hidden', ...sx }} {...rest}>{children}</Box>
);

/**
 * The section header with the 4px green bar — the motif that runs down every parent screen, and
 * the quickest way for these screens to stop looking borrowed.
 */
export const SectionHeader = ({ title, children }) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        bgcolor: colour.stage,
        borderBottom: `1px solid ${colour.border}`,
        px: 2,
        py: 1.25,
    }}>
        <Box sx={{ width: 4, height: 20, bgcolor: colour.primary, borderRadius: 2 }} />
        <Typography variant="subtitle1" sx={{
            fontWeight: 700,
            color: colour.ink,
            fontSize: { xs: '14.5px', sm: '16px' },
            flex: 1,
        }}>
            {title}
        </Typography>
        {children}
    </Box>
);

export default AdminPage;
